import React from 'react';
import { Search, X, Filter, Columns2 } from 'lucide-react';
import {
  type Column,
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  type VisibilityState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button } from '@daater/ui';
import {
  type RangeFilter,
  type ColumnFilterValue,
  isDateValue,
  toNumber,
  toDateMs,
  rangeFilter,
  matchesTextFilters,
  matchesRangeFilters,
} from './data-table.utils';

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  cellMaxWidth?: number;
  initialColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (state: VisibilityState) => void;
  onFilteredDataChange?: (rows: T[]) => void;
};

const DataTable = <T,>({
  data,
  columns,
  cellMaxWidth = 260,
  initialColumnVisibility,
  onColumnVisibilityChange,
  onFilteredDataChange,
}: DataTableProps<T>) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [activeFilterColumn, setActiveFilterColumn] = React.useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialColumnVisibility ?? {}
  );
  const [columnPickerOpen, setColumnPickerOpen] = React.useState(false);
  const [columnSearch, setColumnSearch] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  // Sync with initialColumnVisibility when it changes (e.g., table switch)
  React.useEffect(() => {
    if (initialColumnVisibility) {
      setColumnVisibility(initialColumnVisibility);
    }
  }, [initialColumnVisibility]);

  const handleColumnVisibilityChange = React.useCallback(
    (updaterOrValue: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
      const newVisibility =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;

      setColumnVisibility(newVisibility);

      // Notify parent immediately so export/save use the current state.
      // The parent debounces the settings mutation separately.
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(newVisibility);
      }
    },
    [onColumnVisibilityChange, columnVisibility]
  );

  React.useEffect(() => {
    setPageIndex(0);
  }, [globalFilter, columnFilters]);

  const tableColumns = React.useMemo<ColumnDef<T, unknown>[]>(
    () =>
      columns.map(column => ({
        ...column,
        filterFn:
          column.filterFn && column.filterFn !== 'includesString' ? column.filterFn : 'textMulti',
      })),
    [columns]
  );

  const isRangeFilter = (value: unknown): value is RangeFilter =>
    typeof value === 'object' && value !== null && ('min' in value || 'max' in value);
  const isRangeFilterArray = (value: unknown): value is RangeFilter[] =>
    Array.isArray(value) && value.every(item => isRangeFilter(item));
  const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every(item => typeof item === 'string');

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination: { pageIndex, pageSize },
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onPaginationChange: updater => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      numberRange: (row, columnId, value) => {
        if (isRangeFilterArray(value)) {
          return matchesRangeFilters(row.getValue(columnId), value, toNumber);
        }
        if (isRangeFilter(value)) {
          return rangeFilter(row, columnId, value, toNumber);
        }
        return true;
      },
      dateRange: (row, columnId, value) => {
        if (isRangeFilterArray(value)) {
          return matchesRangeFilters(row.getValue(columnId), value, toDateMs);
        }
        if (isRangeFilter(value)) {
          return rangeFilter(row, columnId, value, toDateMs);
        }
        return true;
      },
      textMulti: (row, columnId, value) => {
        if (isStringArray(value)) {
          return matchesTextFilters(row.getValue(columnId), value);
        }
        if (typeof value === 'string') {
          return matchesTextFilters(row.getValue(columnId), [value]);
        }
        return true;
      },
    },
  });

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.closest('[data-filter-popover]') ||
        target.closest('[data-filter-trigger]') ||
        target.closest('[data-column-visibility]') ||
        target.closest('[data-column-trigger]')
      ) {
        return;
      }
      setActiveFilterColumn(null);
      setColumnPickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const allLeafColumns = table.getAllLeafColumns();
  const filteredColumns = React.useMemo(() => {
    const query = columnSearch.trim().toLowerCase();
    if (!query) return allLeafColumns;
    return allLeafColumns.filter(column => {
      const header = column.columnDef.header;
      const label =
        typeof header === 'string'
          ? header
          : typeof header === 'number'
            ? String(header)
            : column.id;
      return label.toLowerCase().includes(query);
    });
  }, [allLeafColumns, columnSearch]);

  const filteredRows = table.getFilteredRowModel().rows;

  React.useEffect(() => {
    if (!onFilteredDataChange) return;
    onFilteredDataChange(filteredRows.map(row => row.original));
  }, [filteredRows, onFilteredDataChange]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center w-full max-w-3xl gap-2">
          <button
            type="button"
            title="Buscar en los datos"
            className="inline-flex items-center justify-center w-10 h-10 transition bg-white border rounded-lg border-pale-grey-three text-slate hover:border-primary/40 hover:text-primary"
            onClick={() => searchRef.current?.focus()}
          >
            <Search className="w-4 h-4" />
          </button>
          <input
            ref={searchRef}
            value={globalFilter ?? ''}
            onChange={event => setGlobalFilter(event.target.value)}
            placeholder="Buscar en todos los datos..."
            className="w-full px-3 py-2 text-sm bg-white border rounded-lg shadow-sm border-pale-grey-three text-charcoal-grey focus:border-primary focus:ring focus:ring-primary/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative" data-column-visibility>
            <Button
              variant="outline"
              size="sm"
              data-column-trigger
              onClick={() => setColumnPickerOpen(prev => !prev)}
            >
              <Columns2 className="mr-2 h-4 w-4" />
              Columnas ({visibleColumnCount})
            </Button>
            {columnPickerOpen ? (
              <div
                className="absolute left-0 z-40 w-64 p-3 text-xs bg-white border rounded-lg shadow-lg top-10 border-pale-grey-three"
                onMouseDown={event => event.stopPropagation()}
                data-column-visibility
              >
                <div className="mb-2 text-xs font-semibold uppercase text-slate">
                  Columnas visibles
                </div>
                <input
                  type="text"
                  value={columnSearch}
                  onChange={event => setColumnSearch(event.target.value)}
                  placeholder="Buscar columna..."
                  className="w-full px-2 py-1 mb-3 text-xs bg-white border rounded-md border-pale-grey-three"
                />
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-[11px] rounded-md text-slate hover:bg-pale-grey"
                    onClick={() => table.toggleAllColumnsVisible(true)}
                  >
                    Mostrar todo
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 text-[11px] rounded-md text-slate hover:bg-pale-grey"
                    onClick={() => {
                      const first = allLeafColumns[0];
                      if (!first) return;
                      const next: VisibilityState = {};
                      allLeafColumns.forEach(column => {
                        next[column.id] = column.id === first.id;
                      });
                      handleColumnVisibilityChange(next);
                    }}
                  >
                    Solo 1
                  </button>
                </div>
                <div className="max-h-56 space-y-2 overflow-auto pr-1">
                  {filteredColumns.map(column => {
                    const header = column.columnDef.header;
                    const label =
                      typeof header === 'string'
                        ? header
                        : typeof header === 'number'
                          ? String(header)
                          : column.id;
                    const isVisible = column.getIsVisible();
                    const canHide = column.getCanHide?.() ?? true;
                    return (
                      <label key={column.id} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={!canHide}
                          onChange={() => {
                            handleColumnVisibilityChange({
                              ...columnVisibility,
                              [column.id]: !isVisible,
                            });
                          }}
                        />
                        <span className={`text-charcoal-grey ${!canHide ? 'opacity-60' : ''}`}>
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              table.resetColumnFilters();
              setGlobalFilter('');
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      </div>
      {visibleColumnCount === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-pale-grey-three bg-pale-grey/40 px-4 py-12 text-center">
          <Columns2 className="h-10 w-10 text-slate/50" />
          <div>
            <p className="text-sm font-semibold text-charcoal-grey">
              No hay columnas seleccionadas
            </p>
            <p className="mt-1 text-xs text-slate">
              Selecciona al menos una columna desde el menu de columnas para visualizar los datos.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-primary/90"
            onClick={() => table.toggleAllColumnsVisible(true)}
          >
            Mostrar todas las columnas
          </button>
        </div>
      ) : (
        <div className="max-h-[520px] overflow-auto bg-white border rounded-lg border-pale-grey-three">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-pale-grey">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-3 py-3 text-xs font-semibold uppercase border-b border-pale-grey-three text-slate"
                    >
                      <div className="relative space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <button
                            className="flex items-center gap-1 text-left"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{ asc: '↑', desc: '↓' }[header.column.getIsSorted() as string] ?? null}
                          </button>
                          <button
                            type="button"
                            data-filter-trigger
                            onClick={() =>
                              setActiveFilterColumn(prev =>
                                prev === header.column.id ? null : header.column.id
                              )
                            }
                            className="inline-flex items-center justify-center w-6 h-6 transition border border-transparent rounded-md text-slate hover:border-pale-grey-three hover:text-primary"
                            title="Agregar filtro"
                          >
                            <Filter className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <FilterBadges
                          value={header.column.getFilterValue() as ColumnFilterValue}
                          onRemove={valueToRemove => {
                            const current =
                              (header.column.getFilterValue() as ColumnFilterValue) ?? [];
                            if (Array.isArray(current)) {
                              const next = current.filter(
                                item => JSON.stringify(item) !== JSON.stringify(valueToRemove)
                              );
                              header.column.setFilterValue(next.length ? next : undefined);
                            }
                          }}
                        />
                        {activeFilterColumn === header.column.id ? (
                          <FilterPopover
                            column={header.column}
                            table={table}
                            onClose={() => setActiveFilterColumn(null)}
                          />
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className="odd:bg-white even:bg-pale-grey">
                  {row.getVisibleCells().map(cell => {
                    const cellValue = cell.getValue();
                    const cellTitle =
                      typeof cellValue === 'string' ||
                      typeof cellValue === 'number' ||
                      typeof cellValue === 'boolean'
                        ? String(cellValue)
                        : '';
                    return (
                      <td key={cell.id} className="px-3 py-2 min-w-32 text-charcoal-grey">
                        <div
                          className="truncate whitespace-nowrap"
                          style={{ maxWidth: cellMaxWidth }}
                          title={cellTitle}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate">
        <span>
          Mostrando{' '}
          {table.getRowModel().rows.length
            ? `${pageIndex * pageSize + 1} - ${Math.min(
                (pageIndex + 1) * pageSize,
                table.getFilteredRowModel().rows.length
              )}`
            : '0'}{' '}
          de {table.getFilteredRowModel().rows.length} resultados
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs">Filas por página:</span>
            <select
              className="px-2 py-1 text-xs bg-white border rounded-md border-pale-grey-three text-charcoal-grey"
              value={pageSize}
              onChange={event => table.setPageSize(Number(event.target.value))}
            >
              {[10, 20, 50].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Ir a página:</span>
            <input
              type="number"
              min={1}
              max={table.getPageCount()}
              value={pageIndex + 1}
              onChange={event => {
                const page = Number(event.target.value);
                if (!page) return;
                table.setPageIndex(page - 1);
              }}
              className="w-16 px-2 py-1 text-xs bg-white border rounded-md border-pale-grey-three text-charcoal-grey"
            />
            <span className="text-xs">de {table.getPageCount()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              ‹
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              ›
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterBadges = ({
  value,
  onRemove,
}: {
  value: ColumnFilterValue;
  onRemove: (valueToRemove: string | RangeFilter) => void;
}) => {
  if (!value || !Array.isArray(value) || value.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {value.map((item, index) => {
        const label = typeof item === 'string' ? item : `${item.min ?? '∞'} – ${item.max ?? '∞'}`;
        return (
          <span
            key={`${label}-${index}`}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary"
          >
            {label}
            <button
              type="button"
              onClick={() => onRemove(item)}
              className="rounded-full bg-white/80 p-0.5 text-primary"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
};

const FilterPopover = <TData,>({
  table,
  column,
  onClose,
}: {
  table: TableType<TData>;
  column: Column<TData, unknown>;
  onClose: () => void;
}) => {
  const columnId = column.id;
  const sample = table.getPreFilteredRowModel().rows[0]?.getValue(columnId);
  const isNumber = typeof sample === 'number';
  const isDate = isDateValue(sample);
  const [textValue, setTextValue] = React.useState('');
  const [rangeValue, setRangeValue] = React.useState<RangeFilter>({
    min: '',
    max: '',
  });

  const addFilter = () => {
    const current = (column.getFilterValue() as ColumnFilterValue) ?? [];
    if (isNumber || isDate) {
      if (!rangeValue.min && !rangeValue.max) return;
      const next = Array.isArray(current) ? [...current, rangeValue] : [rangeValue];
      column.setFilterValue(next);
      setRangeValue({ min: '', max: '' });
      return;
    }
    if (!textValue.trim()) return;
    const next = Array.isArray(current) ? [...current, textValue.trim()] : [textValue.trim()];
    column.setFilterValue(next);
    setTextValue('');
  };

  React.useEffect(() => {
    if (isNumber || isDate) {
      column.setFilterValue((prev: ColumnFilterValue) => prev ?? []);
      return;
    }
  }, [column, isNumber, isDate]);

  return (
    <div
      data-filter-popover
      className="absolute right-0 z-30 w-64 p-3 text-xs bg-white border rounded-lg shadow-lg top-8 border-pale-grey-three"
    >
      <div className="mb-2 text-xs font-semibold uppercase text-slate">Agregar filtro</div>
      {isNumber || isDate ? (
        <div className="space-y-2">
          <input
            type={isDate ? 'date' : 'number'}
            value={rangeValue.min ?? ''}
            onChange={event => setRangeValue(prev => ({ ...prev, min: event.target.value }))}
            placeholder="Min"
            className="w-full px-2 py-1 border rounded-md border-pale-grey-three"
          />
          <input
            type={isDate ? 'date' : 'number'}
            value={rangeValue.max ?? ''}
            onChange={event => setRangeValue(prev => ({ ...prev, max: event.target.value }))}
            placeholder="Max"
            className="w-full px-2 py-1 border rounded-md border-pale-grey-three"
          />
        </div>
      ) : (
        <input
          type="text"
          value={textValue}
          onChange={event => setTextValue(event.target.value)}
          placeholder="Escribe un valor"
          className="w-full px-2 py-1 border rounded-md border-pale-grey-three"
        />
      )}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          className="px-2 py-1 text-xs rounded-md text-slate hover:bg-pale-grey"
          onClick={onClose}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-3 py-1 text-xs font-semibold text-white rounded-md bg-primary"
          onClick={addFilter}
        >
          Agregar
        </button>
      </div>
    </div>
  );
};

export default DataTable;
